import React from 'react'
import * as Sentry from '@sentry/react'
import { extraErrorDataIntegration } from '@sentry/integrations'

import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import { getSentryFeature } from './useFeatures'

const {
  REACT_APP_NODE_ENV,
  REACT_APP_SENTRY_CLIENT,
  REACT_APP_SENTRY_ERROR_RATE,
  REACT_APP_SENTRY_TRACES_RATE,
} = process.env

const errorBlockList = [
  'A senha deve ter no máximo 30 caracteres (combine letras e números).',
  'A senha não deve conter caracteres sequenciais (Ex: abc).',
  'A sua nova senha não pode ser igual a senha atual.',
  'Access Code not found.',
  "Can't find variable: gmo",
  'CPF inválido',
  'CPF inválido. Verifique se você digitou corretamente',
  'CPF já cadastrado',
  'Campo username, code e new_password são obrigatórios',
  'Code: 0',
  'Code: 001',
  'Code: 002',
  'Code: 006',
  'Code: 007',
  'Code: 010',
  'Code: 012',
  'Código expirado. Peça um novo código para a coordenação sua escola.',
  'Código expirado. Peça um novo código à coordenação da sua escola.',
  'Código inválido',
  'E-mail inválido',
  'E-mail já está cadastrado',
  'Erro ao validar o código.',
  'Esse código de acesso já foi utilizado.',
  'Esse e-mail já está sendo utilizado em uma conta de aluno. Por favor, informe outro.',
  'Esse número de celular já está cadastrado.',
  'Esta conta está inativa.',
  'Esta conta não foi ativada.',
  'Este CPF já esta em uso. Verifique se digitou corretamente.',
  'Este código não é válido. Tente novamente.',
  'Este e-mail já está em uso. Tente outro.',
  'Este nome de usuário já está em uso. Tente outro.',
  'Failed to fetch',
  'GraphQL error: Authorization token',
  'Identificamos que você tem um email cadastrado, por favor digite seu email',
  'Java exception was raised during method invocation',
  'Load failed',
  'Login ou senha não encontrados.',
  'Network Error',
  'NetworkError',
  'Não encontramos o seu e-mail ou celular cadastrado, por favor, verifique se você digitou corretamente.',
  'Não foi possivel confirmar autorização do responsável.',
  'Não é possível alterar sua senha, procure a coordenação de sua escola.',
  'Número de celular inválido.',
  'O Código de acesso digitado não corresponde com o enviado.',
  'O código de acesso informado está expirado.',
  'O código de acesso informado já foi utilizado.',
  'O código de validação digitado não foi encontrado.',
  'O nome de usuário deve ter mais de 5 caracteres (letras, números, ponto, hífen ou sublinhado).',
  'Por motivos de segurança, o botão "Criar nova senha" fica ativo por apenas 48h.',
  'Request failed with status code',
  'Senha muito fraca. Combine letras e números (mínimo de 8 caracteres).',
  'Usuário já autorizado.',
  'Usuário não encontrado',
  'Você excedeu a quantidade de tentativas permitidas. Por favor, aguarde um momento antes de reenviar um novo código.',
  'Você solicitou o código de verificação recentemente. Por favor, espere um momento e depois tente pedir o código novamente.',
  'client: undefined - getActivesYears, usuário sem token.',
  'gtag',
  'n is not a function',
]

let environment = REACT_APP_NODE_ENV
if (environment === 'pull_request') {
  environment = 'staging'
}

const SentryAllowedEnvironments = ['production', 'staging']

const beforeSendFiltering = (FTErrorBlocklist: string[]) => (event: any, hint: any) => {
  if (hint?.originalException) {
    let message: string | undefined

    if (typeof hint.originalException === 'string') {
      message = hint.originalException
    } else if (typeof hint.originalException === 'object' && 'message' in hint.originalException) {
      message = (
        hint.originalException as {
          message: string
        }
      ).message
    }

    if (message) {
      const foundBlockListedTerm = !![...errorBlockList, ...FTErrorBlocklist].filter(
        (error: string) => message!.toLowerCase().includes(error.toLowerCase()),
      ).length

      if (foundBlockListedTerm) {
        return null
      }
    }
  }

  return event
}

const SentrySetUser = ({ plurallId }: { plurallId: string }) => {
  Sentry.setUser({
    plurall_id: plurallId,
  })
}

const initSentry = async () => {
  if (!REACT_APP_SENTRY_CLIENT) {
    console.warn('Sentry client not found')
    return
  }

  if (!REACT_APP_SENTRY_ERROR_RATE) {
    console.warn('Sentry error rate not found')
    return
  }

  if (!REACT_APP_SENTRY_TRACES_RATE) {
    console.warn('Sentry traces rate not found')
    return
  }

  if (REACT_APP_SENTRY_CLIENT && environment && SentryAllowedEnvironments.includes(environment)) {
    const features = await getSentryFeature()

    Sentry.init({
      dsn: REACT_APP_SENTRY_CLIENT,
      environment,
      integrations: [
        new Sentry.BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV6Instrumentation(
            React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          ),
        }),
        extraErrorDataIntegration({ depth: 5 }),
      ],
      normalizeDepth: 5,
      denyUrls: [...features.denyUrls],

      sampleRate: +REACT_APP_SENTRY_ERROR_RATE,
      tracesSampleRate: +REACT_APP_SENTRY_TRACES_RATE,
      beforeSend: beforeSendFiltering(features.errorBlocklist),
      ignoreErrors: [...features.ignoreErrors],
    })
  }
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

export { initSentry, SentryRoutes, SentrySetUser }
